import {useMutation, useQuery} from '@tanstack/react-query';
import React, {ReactElement, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import {useSaveRunfile} from '../../../../hooks/useSaveRunfile';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import {ICalculator, ICalculatorField, ICalculatorFieldWithValue, IElementCalculator} from '../../../../incase-domain/elements/calculator';
import {CalculatorInteractions} from '../../../../incase-domain/proptypes/runfile';
import {calculateResult, fetchCalculatorById} from '../../../../service/calculator.service';
import {updateArrowsDisabledAction} from '../../../../store/actions/presentation.actions';
import {runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';

interface CalculatorElementComponentProps {
    element: IElementCalculator;
    device?: DeviceType;
}

function CalculatorElementComponent({element, device = DeviceType.DESKTOP}: CalculatorElementComponentProps): ReactElement {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const saveRunfile = useSaveRunfile();
    const elementInteractionsBySlide = useSelector(runFileElementInteractionsSelector);
    const elementInteractions = useMemo(
        () => (elementInteractionsBySlide?.[element.slideId]?.[element.id] || {}) as CalculatorInteractions,
        [elementInteractionsBySlide, element.slideId, element.id]
    );

    const [calcInputs, setCalcInputs] = useState<ICalculatorFieldWithValue[]>([]);
    const [result, setResult] = useState<number | null>(null);
    const [initialSet, setInitialSet] = useState(false);

    const {data: calculator} = useQuery<ICalculator>(
        ['calculator', element.calculatorId],
        () => fetchCalculatorById(element.calculatorId),
        {enabled: !!element.calculatorId}
    );

    const {mutate: calculate} = useMutation({
        mutationFn: (inputs: ICalculatorFieldWithValue[]) => {
            const mappedInputs: Record<string, number | string> = {};
            inputs.forEach((field: ICalculatorFieldWithValue) => {
                mappedInputs[field.name] = field.value;
            });
            return calculateResult(element.calculatorId, mappedInputs);
        },
        onSuccess: (data, variables) => {
            setResult(data);
            updateRunFile(variables, data);
        },
    });

    useEffect(() => {
        if (calculator?.fields && !initialSet) {
            setCalcInputs(
                calculator.fields.map((field) => ({
                    ...field,
                    value: elementInteractions.calculatorAnswers?.find((ans) => ans.name === field.name)?.value?.toString() || '',
                }))
            );
            setResult(elementInteractions.calculatorResult?.value || null);
            setInitialSet(elementInteractions.calculatorAnswers?.length === calculator.fields.length);
        }
    }, [calculator, elementInteractions.calculatorAnswers, elementInteractions.calculatorResult, initialSet]);

    const handleInputChange = (value: string, field: ICalculatorField): void => {
        let processedValue = value;

        if (field.type === 'number') {
            const numericRegex = /^-?\d*\.?\d*$/;

            if (numericRegex.test(value) || value === '') {
                processedValue = value;
            } else {
                return;
            }
        }

        const updatedCalcInputs = calcInputs.map((input) => {
            if (input.name === field.name) {
                return {...input, value: processedValue};
            }
            return input;
        });
        setCalcInputs(updatedCalcInputs);

        processCalculationIfReady(updatedCalcInputs);
    };

    const processCalculationIfReady = useDebouncedCallback((updatedCalcInputs: ICalculatorFieldWithValue[]): void => {
        const allInputsPresent = updatedCalcInputs.every((field) => field.value);
        if (allInputsPresent) {
            if (calculator?.formula) {
                const res = calculateFormula(updatedCalcInputs);
                setResult(res);
                updateRunFile(updatedCalcInputs, res);
            } else {
                calculate(updatedCalcInputs);
            }
        } else {
            setResult(null);
        }
    }, 500);

    const calculateFormula = (updatedCalcInputs: ICalculatorFieldWithValue[]): number | null => {
        let formulaWithValues = calculator?.formula;

        updatedCalcInputs.forEach((field) => {
            if (field.value !== undefined) {
                formulaWithValues = formulaWithValues?.replace(new RegExp(`\\b${field.name}\\b`, 'g'), field.value.toString());
            }
        });

        try {
            // eslint-disable-next-line no-new-func
            const result = Function(`"use strict"; return (${formulaWithValues})`)();
            return parseFloat(result.toFixed(2));
        } catch (error) {
            return null;
        }
    };

    const updateRunFile = (updatedCalcInputs: ICalculatorFieldWithValue[], result: number | null): void => {
        const updatedInteractions: CalculatorInteractions = {
            ...elementInteractions,
            calculatorAnswers: updatedCalcInputs,
            calculatorResult: {resultLabel: calculator?.resultLabel, value: result},
        };

        saveRunfile(updatedInteractions, element.id, element.slideId, undefined);
    };

    if (!element.calculatorId || !calculator) {
        return <div className="e-label">{t('calculator.notAvailable')}</div>;
    }

    return (
        <div className={`c-calculator ${device === DeviceType.MOBILE && 'c-calculator--mobile'}`}>
            <div className={`c-calculator__header ${device === DeviceType.DESKTOP ? 'u-mt-2' : ''}`}>{calculator?.header}</div>

            <div>
                {calculator?.fields?.map((field) => (
                    <div key={field._id} className="c-calculator__field">
                        <label className="c-calculator__field_label" htmlFor={`calculator-field-${field._id}`}>
                            {field.label}:
                        </label>
                        <input
                            type="text"
                            id={`calculator-field-${field._id}`}
                            className="c-calculator__field_input"
                            value={calcInputs.find((input) => input.name === field.name)?.value || ''}
                            onChange={(e) => handleInputChange(e.target.value, field)}
                            onFocus={() => dispatch(updateArrowsDisabledAction(true))}
                            onBlur={() => {
                                dispatch(updateArrowsDisabledAction(false));
                            }}
                        />
                    </div>
                ))}
            </div>

            <div className="c-calculator__field c-calculator__field--result">
                <label className="c-calculator__field_label" htmlFor={`calculator-result-${calculator._id}`}>
                    {calculator.resultLabel}:
                </label>
                <input
                    type="text"
                    id={`calculator-result-${calculator._id}`}
                    className="c-calculator__field_input"
                    value={result || ''}
                    readOnly
                    onFocus={() => dispatch(updateArrowsDisabledAction(true))}
                    onBlur={() => {
                        dispatch(updateArrowsDisabledAction(false));
                    }}
                />
            </div>
        </div>
    );
}

export default CalculatorElementComponent;
